import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { PatchType } from '../../../types';
import { setPatches } from '../patchesSlice';

interface PatchData {
  patchId: string;
  name: string;
  patchName?: string;
  type?: string;
  storeNumber: string;
  operations?: any[];
}

interface ExtractedPatchData {
  [PatchType.PRODUCT_PRICE]: PatchData[];
  [PatchType.PRODUCT_EXCLUSION]: PatchData[];
  [PatchType.BUNDLE_PRICE]: PatchData[];
  [PatchType.BUNDLE_EXCLUSION]: PatchData[];
}

export const usePatchDataExtractor = () => {
  const dispatch = useDispatch();
  const [patchData, setPatchData] = useState<ExtractedPatchData>({
    [PatchType.PRODUCT_PRICE]: [],
    [PatchType.PRODUCT_EXCLUSION]: [],
    [PatchType.BUNDLE_PRICE]: [],
    [PatchType.BUNDLE_EXCLUSION]: [],
  });

  const extractPatchDataFromStores = useCallback((stores: any[]) => {
    const extractedData: ExtractedPatchData = {
      [PatchType.PRODUCT_PRICE]: [],
      [PatchType.PRODUCT_EXCLUSION]: [],
      [PatchType.BUNDLE_PRICE]: [],
      [PatchType.BUNDLE_EXCLUSION]: [],
    };

    stores.forEach((store) => {
      if (store.assignedMenuDefinitions) {
        store.assignedMenuDefinitions.forEach((menuDef: any) => {
          if (menuDef.patches) {
            menuDef.patches.forEach((patchItem: any) => {
              const patch = patchItem.patch;
              // Check each patch type
              Object.values(PatchType).forEach((patchType) => {
                if (patch.name && patch.name.endsWith(patchType)) {
                  extractedData[patchType].push({
                    ...patch,
                    storeNumber: store.storeNumber,
                    type: patchType,
                  });
                }
              });
            });
          }
        });
      }
    });

    setPatchData(extractedData);
    // Mirrored into Redux because this is the only place that knows which store each patch belongs to,
    // and the pending-changes panel needs that to name the stores an operation reaches. Every type goes
    // in, not just the selected one: a pending operation captured its patch ids when it was created, so
    // the user may well have switched type since.
    dispatch(setPatches(Object.values(extractedData).flat()));
    return extractedData;
  }, [dispatch]);

  const getPatchDataForType = useCallback((patchType: PatchType): PatchData[] => {
    return patchData[patchType] || [];
  }, [patchData]);

  const getPatchIdsForType = useCallback((patchType: PatchType): string[] => {
    return patchData[patchType]?.map(patch => patch.patchId) || [];
  }, [patchData]);

  const clearPatchData = useCallback(() => {
    setPatchData({
      [PatchType.PRODUCT_PRICE]: [],
      [PatchType.PRODUCT_EXCLUSION]: [],
      [PatchType.BUNDLE_PRICE]: [],
      [PatchType.BUNDLE_EXCLUSION]: [],
    });
    dispatch(setPatches([]));
  }, [dispatch]);

  return {
    patchData,
    extractPatchDataFromStores,
    getPatchDataForType,
    getPatchIdsForType,
    clearPatchData,
  };
};